import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom"; // <-- Import Link

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const pricingTiers = [
  {
    name: "ADU Construction",
    highlight: false,
    tag: "Great ROI",
    services: [
      "Garage conversions & detached ADUs",
      "Full permitting handled in-house",
      "Modern finishes & dedicated utilities",
      "Architectural design coordination",
      "4 – 6 month delivery timeline",
      "A-to-Z project management",
    ],
  },
  {
    name: "Full Remodels & Additions",
    highlight: true,
    tag: "Most Popular",
    services: [
      "Complete interior & exterior transformation",
      "Kitchen, bath, flooring & structural work",
      "Custom cabinetry & luxury finishes",
      "Permit processing & inspections",
      "Dedicated project manager",
      "Zero-shortcut craftsmanship",
    ],
  },
  {
    name: "Custom Homes & HOA",
    highlight: false,
    tag: "Premium Scale",
    services: [
      "Ground-up custom home construction",
      "HOA exterior & building envelope",
      "Waterproofing & structural rehabilitation",
      "Balcony / deck repair & restoration",
      "Long-term capital improvement plans",
      "Expert witness & construction management",
    ],
  },
];

const PricingSection = () => (
  <section className="bg-[#f8f9fa] py-10 px-4 md:px-6">
    <div className="max-w-6xl mx-auto">

      {/* Header */}
      <motion.div
        className="text-center mb-12 md:mb-16"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.p
          variants={fadeUp}
          className="text-[#6c757d] uppercase tracking-wider text-xs md:text-sm font-semibold"
        >
          Investment Ranges
        </motion.p>

        <motion.h2
          variants={fadeUp}
          className="text-3xl md:text-5xl font-black text-[#212529] leading-tight mt-2"
        >
          Projects Starting at Six Figures
        </motion.h2>

        <motion.p
          variants={fadeUp}
          className="mt-4 text-[#495057] max-w-2xl mx-auto text-sm md:text-base"
        >
          We are not competing on price. We compete on competence, experience, structure, and integrity.
          Every engagement is a strategic investment in generational craftsmanship.
        </motion.p>
      </motion.div>

      {/* Cards */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {pricingTiers.map((tier, i) => (
          <motion.div
            key={i}
            variants={fadeUp}
            whileHover={{ scale: tier.highlight ? 1.08 : 1.06, y: -6 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className={`relative rounded-2xl overflow-hidden flex flex-col cursor-pointer ${
              tier.highlight
                ? "bg-[#212529] text-white shadow-2xl scale-[1.03] z-10"
                : "bg-[#ffffff] text-[#212529] shadow-md border border-[#dee2e6]"
            }`}
          >
            {/* Top accent bar */}
            <div
              className={`h-1 w-full ${
                tier.highlight ? "bg-[#495057]" : "bg-[#dee2e6]"
              }`}
            />

            <div className="p-6 md:p-8 flex flex-col flex-1">

              {/* Tag */}
              <span
                className={`inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4 md:mb-5 w-fit ${
                  tier.highlight
                    ? "bg-[#343a40] text-white"
                    : "bg-[#e9ecef] text-[#495057]"
                }`}
              >
                {tier.tag}
              </span>

              {/* Name */}
              <h3
                className={`text-lg md:text-xl font-bold mb-1 ${
                  tier.highlight ? "text-white" : "text-[#212529]"
                }`}
              >
                {tier.name}
              </h3>

              {/* Price range */}
              <div
                className={`text-2xl md:text-3xl font-black mb-4 md:mb-6 ${
                  tier.highlight ? "text-white" : "text-[#212529]"
                }`}
              >
                {tier.range}
              </div>

              {/* Divider */}
              <div
                className={`h-px mb-4 md:mb-6 ${
                  tier.highlight ? "bg-white/10" : "bg-[#dee2e6]"
                }`}
              />

              {/* Services list */}
              <ul className="space-y-2 md:space-y-3 flex-1">
                {tier.services.map((svc, j) => (
                  <li key={j} className="flex items-start gap-2 md:gap-3">
                    <span
                      className={`mt-1 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${
                        tier.highlight ? "bg-[#343a40]" : "bg-[#e9ecef]"
                      }`}
                    >
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <path
                          d="M1 4l2 2 4-4"
                          stroke={tier.highlight ? "#ffffff" : "#495057"}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>

                    <span
                      className={`text-sm md:text-base leading-relaxed ${
                        tier.highlight ? "text-[#adb5bd]" : "text-[#6c757d]"
                      }`}
                    >
                      {svc}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button linking to Contact Page */}
              <Link
                to="/contact"
                className={`mt-6 md:mt-8 w-full py-3.5 rounded-full font-bold text-sm md:text-base tracking-wide text-center transition-all duration-300 inline-block ${
                  tier.highlight
                    ? "bg-white text-[#212529] hover:bg-[#e9ecef]"
                    : "bg-[#212529] text-white hover:bg-[#343a40]"
                }`}
              >
                Request a Consultation
              </Link>

            </div>
          </motion.div>
        ))}
      </motion.div>

    {/* Disclaimer */}
<motion.p
  className="text-center text-sm md:text-base text-black mt-8 md:mt-10 max-w-xl mx-auto"
  initial={{ opacity: 0 }}
  whileInView={{ opacity: 1 }}
  viewport={{ once: true }}
  transition={{ delay: 0.5 }}
>
  All ranges are estimates and determined after a detailed consultation.
  <br />
  Ashlar Building Group serves clients across California.
</motion.p>

    </div>
  </section>
);

export default PricingSection;
